import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { IPageRoute, ISplitUrlLocation } from 'interfaces/routesInt';

import urls from '../urls';
import { usePage } from 'hooks/PlatformContext';

import GenerateRoutes from 'routes/generateRoute/GenerateRoutes';
import LoadingComponent from 'components/shared/Loading';
import SuperAdminRoute from 'routes/protected/SuperAdminRoute';

const formatUrl = (url: string) => url.slice(1);

const ManageRoutes: React.FC = () => {
  const { pathname } = useLocation();

  const splitLocation: ISplitUrlLocation = {
    segments: pathname.split('/'),
  };

  const LoginPage = usePage('LoginPage');
  const RegisterPage = usePage('RegisterPage');
  const DashboardPage = usePage('DashboardPage');
  const SalesPage = usePage('SalesPage');
  const InventoryPage = usePage('InventoryPage');
  const ProductsPage = usePage('ProductsPage');
  const FundsPage = usePage('FundsPage');
  const ArchivedStockPage = usePage('ArchivedStockPage');
  const SettingPage = usePage('SettingPage');
  const ClientPage = usePage('ClientPage');
  const ProductCategoryPage = usePage('ProductCategoryPage');
  const DebtPage = usePage('DebtPage');
  const ForgotPasswordPage = usePage('ForgotPasswordPage');
  const PageNotFound = usePage('PageNotFound');
  const SuppliersPage = usePage('SuppliersPage');
  const ManageBranchs = usePage('ManageBranchsPage');
  const StockPage = usePage('StockPage');
  const PatientHistoryPage = usePage('PatientHistoryPage');
  const HospitalReportsPage = usePage('HospitalReportsPage');
  const MerchantStorefrontPage = usePage('MerchantStorefrontPage');

  const RealEstateWorkspacePage = usePage('RealEstateWorkspacePage');
  const BarbershopWorkspacePage = usePage('BarbershopWorkspacePage');
  const SpaWorkspacePage = usePage('SpaWorkspacePage');
  const TailoringWorkspacePage = usePage('TailoringWorkspacePage');
  const ProfessionalServicesWorkspacePage = usePage('ProfessionalServicesWorkspacePage');
  const SalonWorkspacePage = usePage('SalonWorkspacePage');
  const CarRentalWorkspacePage = usePage('CarRentalWorkspacePage');

  const pages: IPageRoute = {
    [formatUrl(urls.landingPage)]: LoginPage,
    [formatUrl(urls.login)]: LoginPage,
    [formatUrl(urls.register)]: RegisterPage,
    [formatUrl(urls.dashboard)]: DashboardPage,
    [formatUrl(urls.sales)]: SalesPage,
    [formatUrl(urls.pos)]: usePage('SalesPageV2'), // Assumed V2 is the main POS
    [formatUrl(urls.salesHistory)]: usePage('StockMovementsPage'), // Sales History & Stock Movements page
    [formatUrl(urls.inventory)]: InventoryPage,
    [formatUrl(urls.products)]: ProductsPage,
    [formatUrl(urls.funds)]: FundsPage,
    [formatUrl(urls.expenses)]: usePage('ExpensesPage'),
    [formatUrl(urls.archivedStock)]: ArchivedStockPage,
    [formatUrl(urls.settings)]: SettingPage,
    [formatUrl(urls.customers)]: ClientPage,
    [formatUrl(urls.productCategory)]: ProductCategoryPage,
    [formatUrl(urls.debts)]: DebtPage,
    [formatUrl(urls.resetPassword)]: ForgotPasswordPage,
    [formatUrl(urls.pageNotFound)]: PageNotFound,
    [formatUrl(urls.suppliers)]: SuppliersPage,
    [formatUrl(urls.manageBranchs)]: ManageBranchs,
    [formatUrl(urls.stock)]: StockPage,
    [formatUrl(urls.salesV2)]: usePage('SalesPageV2'),
    [formatUrl(urls.wholesale)]: usePage('WholesalePage'),
    [formatUrl(urls.purchaseOrders)]: usePage('PurchaseOrdersPage'),
    [formatUrl(urls.createPurchaseOrder)]: usePage('CreatePurchaseOrderPage'),
    [formatUrl(urls.deliveryNotes)]: usePage('DeliveryNotesPage'),
    [formatUrl(urls.createDeliveryNote)]: usePage('CreateDeliveryNotePage'),
    [formatUrl(urls.pharmacyDashboard)]: usePage('PharmacyDashboardPage'),
    [formatUrl(urls.prescriptions)]: usePage('PrescriptionsPage'),
    [formatUrl(urls.createPrescription)]: usePage('CreatePrescriptionPage'),
    [formatUrl(urls.medications)]: usePage('MedicationsPage'),
    [formatUrl(urls.pharmacists)]: usePage('PharmacistsPage'),
    [formatUrl(urls.createPatient)]: usePage('CreatePatientPage'),
    [formatUrl(urls.patients)]: usePage('PatientsPage'),
    [formatUrl(urls.pharmacyBatches)]: usePage('PharmacyBatchesPage'),
    [formatUrl(urls.pharmacyPOS)]: usePage('PharmacyPOSPage'),
    [formatUrl(urls.pharmacySalesHistory)]: usePage('PharmacySalesHistoryPage'),
    [formatUrl(urls.pharmacyBranches)]: usePage('PharmacyBranchesPage'),
    [formatUrl(urls.pharmacyReports)]: usePage('PharmacyReportsPage'),
    [formatUrl(urls.pharmacyDispensing)]: usePage('PharmacyDispensingPage'),
    // Pharmacy Procurement
    [formatUrl(urls.pharmacyPurchaseOrders)]: usePage('PharmacyPurchaseOrdersPage'),
    [formatUrl(urls.pharmacyPurchaseOrderDetail)]: usePage('PharmacyPurchaseOrderDetailPage'),
    [formatUrl(urls.pharmacyStockTransfers)]: usePage('PharmacyStockTransfersPage'),
    [formatUrl(urls.pharmacyCreateStockTransfer)]: usePage('PharmacyCreateStockTransferPage'),
    [formatUrl(urls.pharmacyStockTransferDetail)]: usePage('PharmacyStockTransferDetailPage'),
    [formatUrl(urls.pharmacyGoodsReceiving)]: usePage('PharmacyGoodsReceivingPage'),
    [formatUrl(urls.pharmacyGoodsReceivingCreate)]: usePage('PharmacyGoodsReceivingCreatePage'),
    [formatUrl(urls.pharmacyGoodsReceivingDetail)]: usePage('PharmacyGoodsReceivingDetailPage'),

    // Retail (shop, boutique, store, supermarket)
    [formatUrl(urls.retailInventory)]: usePage('RetailInventoryPage'),
    [formatUrl(urls.storeFulfillment)]: usePage('RetailFulfillmentPage'),
    [formatUrl(urls.retailSuppliers)]: usePage('RetailSuppliersPage'),
    [formatUrl(urls.retailReports)]: usePage('RetailReportsPage'),

    // Coffee Shop Microservice
    [formatUrl(urls.coffeeDashboard)]: usePage('CoffeeShopDashboardPage'),
    [formatUrl(urls.coffeePOS)]: usePage('CoffeeShopPOSPage'),
    [formatUrl(urls.coffeeOrders)]: usePage('CoffeeShopOrdersPage'),
    [formatUrl(urls.coffeeRecipes)]: usePage('CoffeeShopRecipesPage'),
    [formatUrl(urls.coffeeModifiers)]: usePage('CoffeeShopModifiersPage'),
    [formatUrl(urls.coffeeShifts)]: usePage('CoffeeShopShiftsPage'),
    [formatUrl(urls.coffeeSpillage)]: usePage('CoffeeShopSpillagePage'),
    [formatUrl(urls.coffeeLoyalty)]: usePage('CoffeeShopLoyaltyPage'),

    // Domain Microservices - Grocery & Supermarket
    [formatUrl(urls.groceryDashboard)]: usePage('GroceryDashboardPage'),
    [formatUrl(urls.groceryPOS)]: usePage('GroceryPOSPage'),
    [formatUrl(urls.groceryInventory)]: usePage('GroceryInventoryPage'),
    [formatUrl(urls.groceryExpiries)]: usePage('GroceryExpiriesPage'),
    [formatUrl(urls.grocerySpoilage)]: usePage('GrocerySpoilagePage'),
    [formatUrl(urls.grocerySalesHistory)]: usePage('GrocerySalesHistoryPage'),
    [formatUrl(urls.groceryReports)]: usePage('GroceryReportsPage'),
    [formatUrl(urls.clothingDashboard)]: usePage('ClothingPage'),
    [formatUrl(urls.shoeDashboard)]: usePage('ShoePage'),
    [formatUrl(urls.shoeCatalog)]: usePage('ShoeCatalogPage'),
    [formatUrl(urls.shoePOS)]: usePage('ShoePOSPage'),
    [formatUrl(urls.shoePrepacks)]: usePage('ShoePrepacksPage'),
    [formatUrl(urls.shoeAudits)]: usePage('ShoeAuditsPage'),
    [formatUrl(urls.shoeMismatched)]: usePage('ShoeMismatchedPage'),
    [formatUrl(urls.shoeCare)]: usePage('ShoeCarePage'),
    [formatUrl(urls.shoeLabels)]: usePage('ShoeLabelsPage'),
    [formatUrl(urls.shoeSizeConverter)]: usePage('ShoeSizeConverterPage'),
    [formatUrl(urls.shoeSalesHistory)]: usePage('ShoeSalesHistoryPage'),
    'shoe/sales-history': usePage('ShoeSalesHistoryPage'),
    [formatUrl(urls.shoeDebts)]: usePage('ShoeDebtsPage'),
    'shoe/debts': usePage('ShoeDebtsPage'),
    [formatUrl(urls.hardwareDashboard)]: usePage('HardwarePage'),
    [formatUrl(urls.jewelryDashboard)]: usePage('JewelryPage'),
    [formatUrl(urls.jewelryVault)]: usePage('JewelryVaultPage'),
    [formatUrl(urls.jewelryPOS)]: usePage('JewelryPOSPage'),
    [formatUrl(urls.jewelrySalesHistory)]: usePage('JewelrySalesHistoryPage'),
    [formatUrl(urls.jewelryCertificates)]: usePage('JewelryCertificatesPage'),
    [formatUrl(urls.jewelryTradeIns)]: usePage('JewelryTradeInsPage'),
    [formatUrl(urls.jewelrySpotRates)]: usePage('JewelrySpotRatesPage'),
    [formatUrl(urls.jewelryReports)]: usePage('JewelryReportsPage'),

    // Hotel & Hospitality PMS
    [formatUrl(urls.hotelDashboard)]: usePage('HotelDashboardPage'),
    [formatUrl(urls.hotelRooms)]: usePage('HotelRoomsPage'),
    [formatUrl(urls.hotelReservations)]: usePage('HotelReservationsPage'),
    [formatUrl(urls.hotelHousekeeping)]: usePage('HotelHousekeepingPage'),
    [formatUrl(urls.hotelFolios)]: usePage('HotelFoliosPage'),
    [formatUrl(urls.hotelReports)]: usePage('HotelReportsPage'),
    [formatUrl(urls.hotelGuests)]: usePage('HotelGuestsPage'),

    // 7 New Business Verticals
    [formatUrl(urls.realEstateDashboard)]: RealEstateWorkspacePage,
    [formatUrl(urls.realEstateProperties)]: RealEstateWorkspacePage,
    [formatUrl(urls.realEstateUnits)]: RealEstateWorkspacePage,
    [formatUrl(urls.realEstateRentPayments)]: RealEstateWorkspacePage,
    [formatUrl(urls.realEstateMaintenance)]: RealEstateWorkspacePage,

    [formatUrl(urls.barbershopDashboard)]: BarbershopWorkspacePage,
    [formatUrl(urls.barbershopQueue)]: BarbershopWorkspacePage,
    [formatUrl(urls.barbershopChairs)]: BarbershopWorkspacePage,
    [formatUrl(urls.barbershopServices)]: BarbershopWorkspacePage,

    [formatUrl(urls.spaDashboard)]: SpaWorkspacePage,
    [formatUrl(urls.spaBookings)]: SpaWorkspacePage,
    [formatUrl(urls.spaRooms)]: SpaWorkspacePage,
    [formatUrl(urls.spaTreatments)]: SpaWorkspacePage,
    [formatUrl(urls.spaMemberships)]: SpaWorkspacePage,

    [formatUrl(urls.tailoringDashboard)]: TailoringWorkspacePage,
    [formatUrl(urls.tailoringOrders)]: TailoringWorkspacePage,
    [formatUrl(urls.tailoringMeasurements)]: TailoringWorkspacePage,
    [formatUrl(urls.tailoringFabrics)]: TailoringWorkspacePage,

    [formatUrl(urls.professionalServicesDashboard)]: ProfessionalServicesWorkspacePage,
    [formatUrl(urls.professionalServicesProjects)]: ProfessionalServicesWorkspacePage,
    [formatUrl(urls.professionalServicesTimesheets)]: ProfessionalServicesWorkspacePage,
    [formatUrl(urls.professionalServicesRetainers)]: ProfessionalServicesWorkspacePage,

    [formatUrl(urls.salonDashboard)]: SalonWorkspacePage,
    [formatUrl(urls.salonAppointments)]: SalonWorkspacePage,
    [formatUrl(urls.salonStations)]: SalonWorkspacePage,
    [formatUrl(urls.salonServices)]: SalonWorkspacePage,
    [formatUrl(urls.salonFormulas)]: SalonWorkspacePage,

    [formatUrl(urls.carRentalDashboard)]: CarRentalWorkspacePage,
    [formatUrl(urls.carRentalVehicles)]: CarRentalWorkspacePage,
    [formatUrl(urls.carRentalAgreements)]: CarRentalWorkspacePage,
    [formatUrl(urls.carRentalRentals)]: CarRentalWorkspacePage,
    [formatUrl(urls.carRentalInspections)]: CarRentalWorkspacePage,

    // Branch Management
    [formatUrl(urls.branchPricing)]: usePage('BranchPricingPage'),
    [formatUrl(urls.branchTransfers)]: usePage('BranchTransfersPage'),

    [formatUrl(urls.barDashboard)]: usePage('BarDashboardPage'),
    [formatUrl(urls.barPOS)]: usePage('BarPOSPage'),
    [formatUrl(urls.comptoirs)]: usePage('ComptoirsPage'),
    [formatUrl(urls.barStock)]: usePage('BarStockPage'),
    [formatUrl(urls.barMovements)]: usePage('BarMovementsPage'),
    [formatUrl(urls.barRecipes)]: usePage('BarRecipesPage'),
    [formatUrl(urls.recipeDetails)]: usePage('BarRecipeDetailsPage'),
    [formatUrl(urls.createRecipe)]: usePage('BarCreateRecipePage'),
    [formatUrl(urls.barRecipeEdit)]: usePage('BarCreateRecipePage'),
    [formatUrl(urls.barTabs)]: usePage('BarTabsPage'),
    [formatUrl(urls.barShifts)]: usePage('BarShiftsPage'),
    [formatUrl(urls.barAnalytics)]: usePage('BarAnalyticsPage'),
    [formatUrl(urls.barMenus)]: usePage('BarMenusPage'),
    [formatUrl(urls.barManageMenu)]: usePage('BarManageMenuPage'),
    [formatUrl(urls.barBottles)]: usePage('BarBottlesPage'),
    
    // Analytics routes - Direct import to ensure availability
    [formatUrl(urls.salesAnalytics)]: usePage('SalesAnalyticsPage'),
    [formatUrl(urls.inventoryAnalytics)]: usePage('InventoryAnalyticsPage'),
    [formatUrl(urls.customerAnalytics)]: usePage('CustomerAnalyticsPage'),
    [formatUrl(urls.financialAnalytics)]: usePage('FinancialAnalyticsPage'),
    [formatUrl(urls.stockMovements)]: usePage('StockMovementsPage'),
    [formatUrl(urls.patientHistory)]: usePage('PatientHistoryPage'),
    [formatUrl(urls.auditLogs)]: usePage('AuditLogsPage'),
    [formatUrl(urls.reports)]: usePage('ReportsPage'),
    // Owner Dashboard
    [formatUrl(urls.ownerDashboard)]: usePage('OwnerDashboardPage'),
    // Website Management
    [formatUrl(urls.websitePricingPlans)]: usePage('WebsitePricingPlansPage'),
    [formatUrl(urls.websiteVisitors)]: usePage('WebsiteVisitorsPage'),
    [formatUrl(urls.subscriptionRenew)]: usePage('SubscriptionRenewPage'),
    
    // Super Admin Portal
    [formatUrl(urls.superAdminLogin)]: usePage('SuperAdminLoginPage'),
    [formatUrl('/sup-admin/login')]: usePage('SuperAdminLoginPage'),
    [formatUrl('/sup-admin')]: usePage('SuperAdminLoginPage'),
    [formatUrl(urls.superAdminDashboard)]: (
      <SuperAdminRoute>
        {usePage('SuperAdminDashboardPage')}
      </SuperAdminRoute>
    ),
    [formatUrl(urls.superAdminShops)]: (
      <SuperAdminRoute>
        {usePage('SuperAdminShopsPage')}
      </SuperAdminRoute>
    ),
    [formatUrl(urls.superAdminUsers)]: (
      <SuperAdminRoute>
        {usePage('SuperAdminUsersPage')}
      </SuperAdminRoute>
    ),
    [formatUrl(urls.superAdminActivity)]: (
      <SuperAdminRoute>
        {usePage('SuperAdminActivityPage')}
      </SuperAdminRoute>
    ),
    [formatUrl(urls.superAdminPayments)]: (
      <SuperAdminRoute>
        {usePage('SuperAdminPaymentsPage')}
      </SuperAdminRoute>
    ),
    [formatUrl(urls.superAdminSettings)]: (
      <SuperAdminRoute>
        {usePage('SuperAdminSettingsPage')}
      </SuperAdminRoute>
    ),
    [formatUrl(urls.superAdminStorefronts)]: (
      <SuperAdminRoute>
        {usePage('SuperAdminStorefrontsPage')}
      </SuperAdminRoute>
    ),

    // Restaurant
    [formatUrl(urls.restaurantDashboard)]: usePage('RestaurantDashboardPage'),
    [formatUrl(urls.restaurantTables)]: usePage('RestaurantTablesPage'),
    [formatUrl(urls.restaurantReservations)]: usePage('RestaurantReservationsPage'),
    [formatUrl(urls.restaurantKitchen)]: usePage('RestaurantKitchenPage'),
    [formatUrl(urls.restaurantTableReports)]: usePage('RestaurantTableReportsPage'),
    [formatUrl(urls.restaurantTableTerms)]: usePage('RestaurantTableTermsPage'),
    [formatUrl(urls.guestOrder)]: usePage('GuestMenuPage'),
    // Hospital & Clinical
    [formatUrl(urls.hospitalDashboard)]: usePage('HospitalClinicalDashboardPage'),
    [formatUrl(urls.hospitalReports)]: HospitalReportsPage,
    [formatUrl(urls.hospitalQueue)]: usePage('HospitalQueuePage'),
    [formatUrl(urls.hospitalAppointments)]: usePage('HospitalAppointmentsPage'),
    [formatUrl(urls.hospitalTriage)]: usePage('HospitalTriagePage'),
    [formatUrl(urls.hospitalConsultation)]: usePage('HospitalConsultationPage'),
    'hospital/consultation': usePage('HospitalQueuePage'), // Fallback for list view
    'hospital/triage': usePage('HospitalQueuePage'), // Fallback for list view
    [formatUrl(urls.hospitalHR)]: usePage('HRMEmployeesPage'),
    [formatUrl(urls.hospitalPayroll)]: usePage('HRMPayrollPage'), 
    [formatUrl(urls.hospitalManagement)]: usePage('SettingPage'),
    [formatUrl(urls.hospitalWards)]: usePage('HospitalWardsPage'),
    [formatUrl(urls.hospitalLabs)]: usePage('HospitalLabsPage'),
    [formatUrl(urls.hospitalFinance)]: usePage('HospitalFinancePage'),
    [formatUrl(urls.hospitalPACS)]: usePage('HospitalPACSPage'),
    [formatUrl(urls.hospitalSettings)]: usePage('HospitalSettingsPage'),
    [formatUrl(urls.hospitalAmbulance)]: usePage('HospitalAmbulancePage'),
    [formatUrl(urls.hrmEmployees)]: usePage('HRMEmployeesPage'),
    [formatUrl(urls.hrmPayroll)]: usePage('HRMPayrollPage'),
    [formatUrl(urls.shoeSalesHistory)]: usePage('ShoeSalesHistoryPage'),
    [formatUrl(urls.hardwareDashboard)]: usePage('HardwareDashboardPage'),
    [formatUrl(urls.hardwareCatalog)]: usePage('HardwarePage'),
    'hardware/catalog': usePage('HardwarePage'),
    [formatUrl(urls.hardwareRentals)]: usePage('HardwareRentalsPage'),
    'hardware/rentals': usePage('HardwareRentalsPage'),
    'tool-rentals': usePage('HardwareRentalsPage'),
    'hardware/pos': usePage('SalesPageV2'),
    'hardware/debts': usePage('HardwareDebtsPage'),
    'hardware/analytics': usePage('HardwareAnalyticsPage'),
    'hardware/customers': usePage('HardwareCustomersPage'),
    'hardware/reports': usePage('HardwareReportsPage'),

    // Mobile Money & Agency Banking
    [formatUrl(urls.mobileMoneyDashboard)]: usePage('MobileMoneyDashboardPage'),
    [formatUrl(urls.mobileMoneyPOS)]: usePage('MobileMoneyPOSPage'),
    [formatUrl(urls.mobileMoneyFloats)]: usePage('MobileMoneyFloatsPage'),
    [formatUrl(urls.mobileMoneyLedger)]: usePage('MobileMoneyLedgerPage'),
    [formatUrl(urls.mobileMoneyReconciliation)]: usePage('MobileMoneyReconciliationPage'),
    [formatUrl(urls.mobileMoneyDebts)]: usePage('MobileMoneyDebtsPage'),
    [formatUrl(urls.mobileMoneyCustomerHistory)]: usePage('MobileMoneyCustomerHistoryPage'),
    [formatUrl(urls.mobileMoneyTransactionsCustomerHistory)]: usePage('MobileMoneyCustomerHistoryPage'),
    [formatUrl(urls.mobileMoneyReports)]: usePage('MobileMoneyReportsPage'),
    'mobile-money/reports': usePage('MobileMoneyReportsPage'),

    // FUKKONI Online Storefront & Social Commerce
    [formatUrl(urls.merchantOnlineStore)]: MerchantStorefrontPage,
    [formatUrl(urls.storefrontProducts)]: MerchantStorefrontPage,
    [formatUrl(urls.storefrontOrders)]: MerchantStorefrontPage,
    [formatUrl(urls.storefrontSettings)]: MerchantStorefrontPage,
    'online-store': MerchantStorefrontPage,
    'online-store/products': MerchantStorefrontPage,
    'online-store/orders': MerchantStorefrontPage,
    'settings/storefront': MerchantStorefrontPage,
  };

  return (
    <Suspense fallback={<LoadingComponent />}>
      <GenerateRoutes splitLocation={splitLocation} pageRoute={pages} />
    </Suspense>
  );
};

export default ManageRoutes;
